import { Link } from "@tanstack/react-router";
import { cn } from "mcn";

import { LiveChartsIcon, ManagePagesIcon, MarketIcon, SignOutIcon } from "../icons";
import { ToggleGridSettingsButton } from "../grid-settings/ToggleGridSettingsButton";
import { Tooltip, TooltipTrigger } from "@artis/ui";
import { SidebarIconWrapper } from "./SidebarIconWrapper";
import { isMobile } from "../../shared/hooks";
import { ActionsMenu } from "../components/ActionsMenu";
import { useGridSettingsVisible } from "../grid-settings/hooks";
import { CatalogueIcon } from "../icons/CatalogueIcon";
import { InfoPopup } from "../infoSection";
import { CurveManagementNavButton } from "./CurveManagementNavButton";
import { useCurrentUser, useUserHasRole, useUserSubscriptionTier } from "../../data/user";

export const Sidebar = () => {
  const { user } = useCurrentUser();
  const { disabledFeatures } = useUserSubscriptionTier();
  const [, setGridSettingsVisible] = useGridSettingsVisible();

  const username = user?.username;
  const email = user?.email;

  const isManagePagesDisabled = disabledFeatures?.managePages;
  const isChartsDisabled = disabledFeatures?.charts;
  const isCatalogueDisabled = disabledFeatures?.catalogue;

  const isManager = useUserHasRole("manager");
  const isLiveChartsPage = window.location.pathname.startsWith("/app/trading-chart");

  const openInNewWindow = (path: string, isDisabled: boolean | undefined) => {
    if (isDisabled) return;
    window.open(`https://folio.artis.works${path}`, "_blank", "location=yes,scrollbars=yes,status=yes");
  };

  if (window.location.href.endsWith("/components") || isLiveChartsPage) {
    return null;
  }

  return (
    <div
      className={cn(
        "bg-foreground dark:bg-background h-dvh w-side-bar hidden md:flex flex-col justify-between py-4 pl-safe-left pr-safe-right items-center",
        "dark:border-r border-divider",
        {
          "fixed left-0 top-0 z-[110]": isLiveChartsPage,
          "z-50": !isLiveChartsPage,
        },
      )}
      style={{
        transition: "transform 0.3s ease-in-out",
        transform: "translateX(0)",
      }}
    >
      <div
        className={`flex flex-col gap-[var(--spacing-scale-small-400,12px)] flex-1 items-center ${isMobile ? "pl-safe-left" : ""}`}
      >
        <div className="flex items-center justify-center">
          <img src="/icons/icon-Artis.svg" alt="Artis Icon" width={"70%"} />
        </div>
        <TooltipTrigger placement="right">
          <SidebarIconWrapper>
            <Link to="/app/market">
              <MarketIcon />
            </Link>
          </SidebarIconWrapper>
          <Tooltip>Market</Tooltip>
        </TooltipTrigger>
        {isManager && (
          <TooltipTrigger placement="right">
            <SidebarIconWrapper>
              <button
                type="button"
                className={isManagePagesDisabled ? "cursor-not-allowed" : ""}
                onClick={() => openInNewWindow("/manage-pages", isManagePagesDisabled)}
              >
                <ManagePagesIcon disabled={isManagePagesDisabled} />
              </button>
            </SidebarIconWrapper>
            <Tooltip>
              {isManagePagesDisabled ? "Please upgrade your subscription to access this feature" : "Manage Pages"}
            </Tooltip>
          </TooltipTrigger>
        )}
        <CurveManagementNavButton />
        <TooltipTrigger placement="right">
          <SidebarIconWrapper>
            <Link
              to="/app/trading-chart"
              target="_blank"
              rel="noopener noreferrer"
              className={isChartsDisabled ? "cursor-not-allowed pointer-events-none" : ""}
              onClick={(e) => {
                if (isChartsDisabled) {
                  e.preventDefault();
                }
              }}
            >
              <LiveChartsIcon disabled={isChartsDisabled} />
            </Link>
          </SidebarIconWrapper>
          <Tooltip>{isChartsDisabled ? "Please upgrade your subscription to access this feature" : "Charts"}</Tooltip>
        </TooltipTrigger>
        <TooltipTrigger placement="right">
          <SidebarIconWrapper onClick={() => setGridSettingsVisible(false)}>
            <Link
              to="/app/catalogue"
              className={isCatalogueDisabled ? "cursor-not-allowed pointer-events-none" : ""}
              onClick={(e) => {
                if (isCatalogueDisabled) {
                  e.preventDefault();
                }
              }}
            >
              <CatalogueIcon disabled={isCatalogueDisabled} />
            </Link>
          </SidebarIconWrapper>
          <Tooltip>
            {isCatalogueDisabled ? "Please upgrade your subscription to access this feature" : "Product Catalogue"}
          </Tooltip>
        </TooltipTrigger>
      </div>
      <div
        className={`flex flex-col gap-[var(--spacing-scale-small-400,12px)] flex-0 items-center ${isMobile ? "pl-safe-left" : ""}`}
      >
        <TooltipTrigger placement="right">
          <div className="max-w-[60px] overflow-hidden">
            <p id="nav-username" className="text-[12px] text-center text-neutral uppercase">
              {username}
            </p>
          </div>
          <Tooltip>{email}</Tooltip>
        </TooltipTrigger>
        <ActionsMenu />
        <ToggleGridSettingsButton />

        <InfoPopup />
        <TooltipTrigger placement="right">
          <SidebarIconWrapper>
            <SignOutIcon />
          </SidebarIconWrapper>
          <Tooltip>Log Out</Tooltip>
        </TooltipTrigger>
      </div>
    </div>
  );
};
